import { getUserDoc } from '@griply/services';
import { getSignInProvider } from '@griply/utils';
import { DocumentReference, getDoc } from 'firebase/firestore';
import { getErrorReporter } from 'shared/services/error-tracking';
import { User } from 'shared/types/user';
import { UserReadError } from 'shared/types/user-read-error';
import { isProductionEnvironment } from 'shared/utils/is-environment';

import { userConverter } from './converters/user';
import { doc, getAuthentication, getDatabase } from './helpers';

export const getUserRef = async () => {
  const auth = await getAuthentication();

  if (!auth.currentUser?.uid) {
    return;
  }

  getErrorReporter().setUser({ id: auth.currentUser.uid });

  return getUserDoc<DocumentReference>({
    db: getDatabase(),
    doc,
    userId: auth.currentUser.uid,
  }).withConverter(userConverter);
};

export const getUser = async () => {
  const start = performance.now().toFixed(0);
  if (!isProductionEnvironment) {
    // eslint-disable-next-line no-console -- non-prod logging
    console.log(`[Debug-GetUser] getUser called at ${start}ms`);
  }

  const auth = await getAuthentication();
  if (!isProductionEnvironment) {
    // eslint-disable-next-line no-console -- non-prod logging
    console.log(
      `[Debug-GetUser] Auth retrieved. currentUser: ${auth.currentUser?.uid}`,
    );
  }

  if (!auth.currentUser) {
    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- non-prod logging
      console.warn('[Debug-GetUser] ❌ No currentUser found. Returning null.');
    }
    return null;
  }

  const userRef = await getUserRef();
  if (!userRef) {
    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- non-prod logging
      console.warn('[Debug-GetUser] ❌ Could not create userRef.');
    }
    return null;
  }

  if (!isProductionEnvironment) {
    // eslint-disable-next-line no-console -- non-prod logging
    console.log(
      `[Debug-GetUser] Attempting getDoc at ${performance.now().toFixed(0)}ms...`,
    );
  }

  try {
    const docSnap = await getDoc(userRef);
    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- non-prod logging
      console.log(`[Debug-GetUser] getDoc result: exists=${docSnap.exists()}`);
    }

    if (!docSnap.exists()) {
      if (!isProductionEnvironment) {
        // eslint-disable-next-line no-console -- non-prod logging
        console.warn('[Debug-GetUser] ❌ Doc does not exist in cache/server.');
      }
      return null;
    }

    const user = docSnap.data() as User;
    user.signInProvider = getSignInProvider(auth.currentUser.providerData);
    user.email = auth.currentUser.email as string;

    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- non-prod logging
      console.log('[Debug-GetUser] ✅ Returning valid user.');
    }
    return user;
  } catch (error) {
    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- non-prod logging
      console.error('[Debug-GetUser] ❌ getDoc threw an error:', error);
    }

    const code = (error as { code?: string }).code;

    getErrorReporter().captureException(error, {
      tags: { source: 'firebase-get-user' },
      extra: { code },
    });

    // Returning null here would be indistinguishable from "no user" and bounce the user to /login.
    throw new UserReadError(code);
  }
};
